import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { FormattedMessage } from 'react-intl';
import Header from '../Header';
import Menu from '../Menu';
import PageTitle from '../PageTitle';
import Divider from '../Divider';
import Sidebar from '../Sidebar';
import Icon from '../Icon';
import Breadcrumb from '../Breadcrumb';
import Image from '../Image';
import LockImage from '../images/ico-lock-x16.png';
import LockedSegment from './locked-segment';
import messages from './messages';
import SubscriptionUpgradeForm from '../../containers/SubscriptionUpgradeForm';

import { active } from './styles.css';

const isPremium = (props, pathname) =>
  (pathname.includes('majors') && !props.hasMajors) ||
  (pathname.includes('competitors') && !props.hasCompetitors) ||
  (pathname.includes('diversity') && !props.hasDiversityEnabled);

const isDisabled = (props, pathname) => (pathname.includes('diversity') && !props.hasDiversityEnabled);

const messagePropType = PropTypes.shape({
  id: PropTypes.string,
  defaultMessage: PropTypes.string,
});
const menuConfigPropType = PropTypes.shape({
  to: PropTypes.string.isRequired,
  message: messagePropType.isRequired,
});
const baseMenuPropType = {
  config: PropTypes.arrayOf(menuConfigPropType),
  className: PropTypes.string,
  hasDiversityEnabled: PropTypes.bool,
};

function getEventTabActiveClass(pathname, path) {
  return pathname.includes(path) ? ' navActive' : '';
}

export const MenuList = (props) => (
  <nav aria-label="Events Sub Menu">
    <Menu
      data-cy="advance-awareness-menu-bar"
      className={props.className}
      stackable
      as="ul"
      pointing
      secondary
      size="huge"
      styleName="menu"
    >
      {props.config.filter((item) => !isDisabled(props, item.to)).map((item) => (
        <Menu.Item key={item.to} as="li" link styleName="menu-item">
          <Link to={item.to} styleName="menu-link" activeClassName={active}>
            <FormattedMessage {...item.message} />
          </Link>
        </Menu.Item>
      ))}
    </Menu>
  </nav>
);
MenuList.propTypes = baseMenuPropType;
MenuList.defaultProps = {
  config: [],
};

export const MenuSide = (props) => (
  <nav aria-label="Events Side Menu">
    <Menu className={props.className} stackable as="ul" pointing secondary size="huge" styleName="navMenu">
      <Menu.Header styleName="navMenu-header">
        <span>{props.title}</span>
        <Icon
          styleName="closeIcon"
          onClick={props.toggleVisibility}
          name="x"
          aria-hidden="true"
          size="large"
        />
      </Menu.Header>
      <Divider styleName="menu-divider" />
      {props.config.map((item, index) => (
        <React.Fragment key={item.to}>
          <Menu.Item as="li" link styleName={`navMenu-item${getEventTabActiveClass(props.pathname, item.to)}`}>
            <Link
              activeClassName={active}
              to={item.to}
              styleName="navMenu-link"
              onClick={props.toggleVisibility}
            >
              <FormattedMessage {...item.message} />
            </Link>
          </Menu.Item>
          {index < props.config.length - 1 && <Divider styleName="menu-divider" />}
        </React.Fragment>
      ))}
    </Menu>
  </nav>
);
MenuSide.propTypes = {
  toggleVisibility: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  title: PropTypes.string,
  ...baseMenuPropType,
};
MenuSide.defaultProps = {
  config: [], /* eslint-disable-line react/default-props-match-prop-types */
};

export const Breadcrumbs = (props) => {
  let message = {};
  const configItem = props.config.find((item) => item.to === props.pathname);
  /* istanbul ignore else */
  if (configItem?.message) {
    message = configItem.message;
  }
  return (
    <Breadcrumb className={props.className}>
      <Breadcrumb.Section>
        <FormattedMessage {...message} />
      </Breadcrumb.Section>
    </Breadcrumb>
  );
};
Breadcrumbs.propTypes = {
  pathname: PropTypes.string,
  ...baseMenuPropType,
};
Breadcrumbs.defaultProps = {
  pathname: '',
  config: [], /* eslint-disable-line react/default-props-match-prop-types */
};

export const SecondMenu = (props) => (
  <nav aria-label="Active Match Sub Menu" data-cy="active-match-sub-menu">
    {props.config.map((area) => (
      <React.Fragment key={area.section.id}>
        <Menu as="ul" vertical className={props.className}>
          <Menu.Item as="li" styleName="second-menu-header">
            <FormattedMessage {...area.section}>
              { (text) => <Header as="h2" size="small">{text}</Header> }
            </FormattedMessage>

          </Menu.Item>
          {area.links.filter((item) => !isDisabled(props, item.to)).map((item) => {
            const isLocked = isPremium(props, item.to);
            return (
              <Menu.Item data-cy={item.to} key={item.to} as="li" styleName={isLocked ? 'premiumMenuItem' : ''}>
                <Link to={item.to} className="menu-link" activeClassName="active">
                  <FormattedMessage {...item.message} />
                  { isLocked && (
                    <span styleName="premium">
                      <FormattedMessage {...messages.premiumFeature} />&nbsp;
                      <Image src={LockImage} aria-hidden="true" styleName="premium-lock-small" />
                    </span>
                  )}
                </Link>
              </Menu.Item>
            );
          })}
        </Menu>
        <div styleName="second-menu-spacer" />
      </React.Fragment>
    ))}
  </nav>
);
const secondMenuConfigPropType = PropTypes.arrayOf(PropTypes.shape({
  section: messagePropType,
  links: PropTypes.arrayOf(menuConfigPropType),
}));
SecondMenu.propTypes = {
  className: PropTypes.string,
  config: secondMenuConfigPropType,
  hasMajors: PropTypes.bool,
  hasCompetitors: PropTypes.bool,
  hasDiversityEnabled: PropTypes.bool,
};
SecondMenu.defaultProps = {
  config: [],
};

class AMConfigureFrame extends React.Component {
  static propTypes = {
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }).isRequired,
    children: PropTypes.node,
    title: PropTypes.string,
    menuConfig: PropTypes.arrayOf(menuConfigPropType),
    header: PropTypes.object,
    subheader: PropTypes.object,
    hideSecondMenuRoutes: PropTypes.array,
    secondMenuConfig: secondMenuConfigPropType,
    hasMajors: PropTypes.bool,
    hasCompetitors: PropTypes.bool,
    hasDiversityEnabled: PropTypes.bool,
  };

  static defaultProps = {
    hideSecondMenuRoutes: [],
  };

  state = {
    isSidebarVisible: false,
    isUpgradeFormVisible: false,
  };

  toggleSidebarVisibility = () => (
    this.setState(
      (previousState) => ({
        isSidebarVisible: !previousState.isSidebarVisible,
      }),
    )
  );

  toggleUpgradeFormVisibility = () => (
    this.setState(
      (previousState) => ({
        isUpgradeFormVisible: !previousState.isUpgradeFormVisible,
      }),
    )
  );

  render() {
    const { isSidebarVisible, isUpgradeFormVisible } = this.state;
    const {
      children,
      title,
      menuConfig,
      header,
      subheader,
      location,
      hideSecondMenuRoutes,
      secondMenuConfig,
      hasMajors,
      hasCompetitors,
      hasDiversityEnabled,
    } = this.props;
    const showSecondMenu = !hideSecondMenuRoutes.includes(location.pathname);
    return (
      <div styleName="frame">
        <PageTitle title={title} category="Presence" />
        <Sidebar.Pushable styleName="pushableContent">
          <Sidebar
            styleName="sideWrapper"
            as={Menu}
            animation="overlay"
            width="thin"
            visible={isSidebarVisible}
            icon="labeled"
            vertical
            inverted
            className="menu visible-mobile-flex visible-tablet-flex hidden-computer"
          >
            <section aria-label="Active Match Side Menu">
              <MenuSide
                pathname={location.pathname}
                toggleVisibility={this.toggleSidebarVisibility}
                toggleSecondaryMenu={this.toggleSecondaryMenu}
                title={title}
                config={menuConfig}
              />
            </section>
          </Sidebar>
          <Sidebar.Pusher dimmed={isSidebarVisible} styleName="pusherContent" className="dimmed-opacity">
            <Icon
              styleName="hamburgerIcon"
              name="content"
              aria-hidden="true"
              size="large"
              className="hidden-computer"
              onClick={this.toggleSidebarVisibility}
            />
            <Breadcrumbs
              styleName="breadcrumbText"
              className="hidden-computer"
              pathname={location.pathname}
              config={menuConfig}
            />
            <Divider className="hidden-computer" />
            <MenuList
              className="hidden-mobile hidden-tablet"
              config={menuConfig}
              hasDiversityEnabled={hasDiversityEnabled}
            />
            <div styleName="top-container">
              <header styleName="header-container">
                <Header as="h1" styleName="page-header" id="pageTitle">
                  <FormattedMessage {...header} />
                </Header>
                <Header.Subheader styleName="sub-header">
                  <FormattedMessage {...subheader} />
                </Header.Subheader>
              </header>
              <section styleName="container" aria-label="Active Match Sub Menu">
                {showSecondMenu && (
                <SecondMenu
                  className="third"
                  config={secondMenuConfig}
                  hasMajors={hasMajors}
                  hasCompetitors={hasCompetitors}
                  hasDiversityEnabled={hasDiversityEnabled}
                />
                )}
                <div styleName={`content${showSecondMenu ? ' margin-left' : ''}`} data-cy="content-isPremium">
                  {
                    isPremium(this.props, location.pathname) ? (
                      <LockedSegment
                        pathname={location.pathname}
                        onUpgradeClick={this.toggleUpgradeFormVisibility}
                      />
                    ):
                      children
                  }
                </div>
              </section>
            </div>
          </Sidebar.Pusher>
        </Sidebar.Pushable>
        {isUpgradeFormVisible && (
        <SubscriptionUpgradeForm
          open
          subscription="advancedawareness"
          onClose={this.toggleUpgradeFormVisibility}
        />
        )}
      </div>
    );
  }
}
export default AMConfigureFrame;
